"use client"
// https://github.com/taqui-786/mixcnui/blob/main/src/components/mdx/Carousel.tsx
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { EmblaOptionsType } from "embla-carousel"
import useEmblaCarousel from "embla-carousel-react"
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react"
import React, {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react"

type CarouselContextProps = {
  carouselOptions?: EmblaOptionsType
  orientation?: "vertical" | "horizontal"
  plugins?: Parameters<typeof useEmblaCarousel>[1]
}

type DirectionOption = "ltr" | "rtl" | undefined

type CarouselContextType = {
  emblaMainApi: ReturnType<typeof useEmblaCarousel>[1]
  mainRef: ReturnType<typeof useEmblaCarousel>[0]
  thumbsRef: ReturnType<typeof useEmblaCarousel>[0]
  scrollNext: () => void
  scrollPrev: () => void
  canScrollNext: boolean
  canScrollPrev: boolean
  activeIndex: number
  onThumbClick: (index: number) => void
  handleKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => void
  orientation: "vertical" | "horizontal"
  direction: DirectionOption
} & CarouselContextProps

const useCarousel = () => {
  const context = useContext(CarouselContext)

  if (!context) {
    throw new Error("useCarousel must be used within a CarouselProvider")
  }

  return context
}

const CarouselContext = createContext<CarouselContextType | null>(null)

// TODO : add support for vertical rtl support for the carousel
// ref : https://github.com/davidjerleke/embla-carousel/issues/784

const Carousel = forwardRef<
  HTMLDivElement,
  CarouselContextProps & React.HTMLAttributes<HTMLDivElement>
>(
  (
    {
      carouselOptions,
      orientation = "horizontal",
      dir,
      plugins,
      children,
      className,
      ...props
    },
    ref
  ) => {
    const [emblaMainRef, emblaMainApi] = useEmblaCarousel(
      {
        ...carouselOptions,
        axis: orientation === "vertical" ? "y" : "x",
        direction: carouselOptions?.direction ?? (dir as DirectionOption),
      },
      plugins
    )

    const [emblaThumbsRef, emblaThumbsApi] = useEmblaCarousel(
      {
        ...carouselOptions,
        axis: orientation === "vertical" ? "y" : "x",
        direction: carouselOptions?.direction ?? (dir as DirectionOption),
        containScroll: "keepSnaps",
        dragFree: true,
      },
      plugins
    )

    const [canScrollPrev, setCanScrollPrev] = useState<boolean>(false)
    const [canScrollNext, setCanScrollNext] = useState<boolean>(false)
    const [activeIndex, setActiveIndex] = useState<number>(0)

    const ScrollNext = useCallback(() => {
      if (!emblaMainApi) {
        return
      }

      emblaMainApi.scrollNext()
    }, [emblaMainApi])

    const ScrollPrev = useCallback(() => {
      if (!emblaMainApi) {
        return
      }

      emblaMainApi.scrollPrev()
    }, [emblaMainApi])

    const direction = carouselOptions?.direction ?? (dir as DirectionOption)

    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        event.preventDefault()

        if (!emblaMainApi) {
          return
        }

        switch (event.key) {
          case "ArrowLeft":
            if (orientation === "horizontal") {
              if (direction === "rtl") {
                ScrollNext()

                return
              }

              ScrollPrev()
            }

            break
          case "ArrowRight":
            if (orientation === "horizontal") {
              if (direction === "rtl") {
                ScrollPrev()

                return
              }

              ScrollNext()
            }

            break
          case "ArrowUp":
            if (orientation === "vertical") {
              ScrollPrev()
            }

            break
          case "ArrowDown":
            if (orientation === "vertical") {
              ScrollNext()
            }

            break
        }
      },
      [emblaMainApi, orientation, direction]
    )

    const onThumbClick = useCallback(
      (index: number) => {
        if (!emblaMainApi || !emblaThumbsApi) {
          return
        }

        emblaMainApi.scrollTo(index)
      },
      [emblaMainApi, emblaThumbsApi]
    )

    const onSelect = useCallback(() => {
      if (!emblaMainApi || !emblaThumbsApi) {
        return
      }

      const selected = emblaMainApi.selectedScrollSnap()

      setActiveIndex(selected)
      emblaThumbsApi.scrollTo(selected)
      setCanScrollPrev(emblaMainApi.canScrollPrev())
      setCanScrollNext(emblaMainApi.canScrollNext())
    }, [emblaMainApi, emblaThumbsApi])

    useEffect(() => {
      if (!emblaMainApi) {
        return
      }

      onSelect()
      emblaMainApi.on("select", onSelect)
      emblaMainApi.on("reInit", onSelect)

      return () => {
        emblaMainApi.off("select", onSelect)
      }
    }, [emblaMainApi, onSelect])

    return (
      <CarouselContext.Provider
        value={{
          emblaMainApi,
          mainRef: emblaMainRef,
          thumbsRef: emblaThumbsRef,
          scrollNext: ScrollNext,
          scrollPrev: ScrollPrev,
          canScrollNext,
          canScrollPrev,
          activeIndex,
          onThumbClick,
          handleKeyDown,
          carouselOptions,
          direction,
          orientation:
            orientation ||
            (carouselOptions?.axis === "y" ? "vertical" : "horizontal"),
        }}
      >
        <div
          {...props}
          tabIndex={0}
          ref={ref}
          onKeyDownCapture={handleKeyDown}
          className={cn(
            "relative grid w-full gap-2 focus_outline-none",
            className
          )}
          dir={direction}
        >
          {children}
        </div>
      </CarouselContext.Provider>
    )
  }
)

Carousel.displayName = "Carousel"

const CarouselMainContainer = forwardRef<
  HTMLDivElement,
  {} & React.HTMLAttributes<HTMLDivElement>
>(({ className, dir, children, ...props }, ref) => {
  const { mainRef, orientation, direction } = useCarousel()

  return (
    <div {...props} ref={mainRef} className="overflow-hidden" dir={direction}>
      <div
        ref={ref}
        className={cn(
          "flex",
          `${orientation === "vertical" ? "flex-col" : ""}`,
          className
        )}
      >
        {children}
      </div>
    </div>
  )
})

CarouselMainContainer.displayName = "CarouselMainContainer"

const CarouselThumbsContainer = forwardRef<
  HTMLDivElement,
  {} & React.HTMLAttributes<HTMLDivElement>
>(({ className, dir, children, ...props }, ref) => {
  const { thumbsRef, orientation, direction } = useCarousel()

  return (
    <div {...props} ref={thumbsRef} className="overflow-hidden" dir={direction}>
      <div
        ref={ref}
        className={cn(
          "flex",
          `${orientation === "vertical" ? "flex-col" : ""}`,
          className
        )}
      >
        {children}
      </div>
    </div>
  )
})

CarouselThumbsContainer.displayName = "CarouselThumbsContainer"

const SliderMainItem = forwardRef<
  HTMLDivElement,
  {} & React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => {
  const { orientation } = useCarousel()

  return (
    <div
      {...props}
      ref={ref}
      className={cn(
        `bg-background min-w-0 shrink-0 grow-0 basis-full`,
        className
      )}
    >
      {children}
    </div>
  )
})

SliderMainItem.displayName = "SliderMainItem"

const SliderThumbItem = forwardRef<
  HTMLDivElement,
  {
    index: number
  } & React.HTMLAttributes<HTMLDivElement>
>(({ className, index, children, ...props }, ref) => {
  const { activeIndex, onThumbClick, orientation } = useCarousel()
  const isSlideActive = activeIndex === index

  return (
    <div
      {...props}
      ref={ref}
      onClick={() => onThumbClick(index)}
      className={cn(
        "bg-background flex min-w-0 shrink-0 grow-0 basis-1/5 p-1",
        `${orientation === "vertical" ? "pb-1" : "pr-1"}`,
        className
      )}
    >
      <div
        className={`relative aspect-square w-full  transition-opacity ${
          isSlideActive
            ? "border-2 border-solid border-primary !opacity-100"
            : "border-1 border-solid border-transparent"
        }`}
      >
        {children}
      </div>
    </div>
  )
})

SliderThumbItem.displayName = "SliderThumbItem"

const CarouselIndicator = forwardRef<
  HTMLButtonElement,
  { index: number } & React.ComponentProps<typeof Button>
>(({ className, index, children, ...props }, ref) => {
  const { activeIndex, onThumbClick } = useCarousel()
  const isSlideActive = activeIndex === index

  return (
    <Button
      ref={ref}
      size="icon"
      className={cn(
        "duration-300 inline-block h-[5px] w-[5px] rounded-[50%] bg-[#c4c4c4] transition-all",
        "data-[active='true']_h-2 data-[active='true']_w-2  data-[active='true']_bg-primary",
        className
      )}
      data-active={isSlideActive}
      type="reset"
      onClick={() => onThumbClick(index)}
      {...props}
    >
      <span className="sr-only">slide {index + 1} </span>
    </Button>
  )
})

CarouselIndicator.displayName = "CarouselIndicator"

const CarouselIndicatorVideoIcon = ({ index }: { index: number }) => {
  const { activeIndex, onThumbClick } = useCarousel()
  const isSlideActive = activeIndex === index

  return (
    <button
      type="button"
      onClick={() => {
        onThumbClick(index)
      }}
    >
      {isSlideActive ? (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="15"
          height="15"
          fill="none"
          viewBox="0 0 15 15"
        >
          <path
            fill="#2E4500"
            d="M13.125 7.5A5.626 5.626 0 0 0 7.5 1.875 5.626 5.626 0 0 0 1.875 7.5 5.626 5.626 0 0 0 7.5 13.125 5.626 5.626 0 0 0 13.125 7.5"
          />
          <path
            fill="#fff"
            d="M6.338 9.798 9.69 7.773a.32.32 0 0 0 0-.545L6.338 5.202a.316.316 0 0 0-.479.273v4.05a.316.316 0 0 0 .479.273"
          />
        </svg>
      ) : (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="15"
          fill="none"
          viewBox="0 0 16 15"
        >
          <path
            stroke="#2E4500"
            strokeMiterlimit="10"
            d="M13.625 7.5A5.626 5.626 0 0 0 8 1.875 5.626 5.626 0 0 0 2.375 7.5 5.626 5.626 0 0 0 8 13.125 5.626 5.626 0 0 0 13.625 7.5Z"
          />
          <path
            fill="#2E4500"
            d="m6.838 9.798 3.353-2.025a.32.32 0 0 0 0-.545L6.838 5.202a.316.316 0 0 0-.479.273v4.05a.316.316 0 0 0 .479.273"
          />
        </svg>
      )}
    </button>
  )
}

const CarouselPrevious = forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof Button>
>(({ className, dir, variant = "outline", size = "icon", ...props }, ref) => {
  const {
    canScrollNext,
    canScrollPrev,
    scrollNext,
    scrollPrev,
    orientation,
    direction,
  } = useCarousel()

  const scroll = direction === "rtl" ? scrollNext : scrollPrev
  const canScroll = direction === "rtl" ? canScrollNext : canScrollPrev

  return (
    <Button
      ref={ref}
      variant={variant}
      size={size}
      className={cn(
        "absolute z-10 h-6 w-6",
        orientation === "vertical"
          ? "-top-2 left-1/2 -translate-x-1/2 rotate-90"
          : "left-6 top-1/2 -translate-y-1/2",
        className
      )}
      type="reset"
      onClick={scroll}
      disabled={!canScroll}
      {...props}
    >
      <ChevronLeftIcon className="h-4 w-4" />
      <span className="sr-only">Previous slide</span>
    </Button>
  )
})

CarouselPrevious.displayName = "CarouselPrevious"

const CarouselNext = forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof Button>
>(({ className, dir, variant = "outline", size = "icon", ...props }, ref) => {
  const {
    canScrollNext,
    canScrollPrev,
    scrollNext,
    scrollPrev,
    orientation,
    direction,
  } = useCarousel()
  const scroll = direction === "rtl" ? scrollPrev : scrollNext
  const canScroll = direction === "rtl" ? canScrollPrev : canScrollNext

  return (
    <Button
      ref={ref}
      variant={variant}
      size={size}
      className={cn(
        "absolute z-10 h-6 w-6",
        orientation === "vertical"
          ? "-bottom-2 left-1/2 -translate-x-1/2 rotate-90"
          : "right-6 top-1/2 -translate-y-1/2",
        className
      )}
      type="button"
      onClick={scroll}
      disabled={!canScroll}
      {...props}
    >
      <ChevronRightIcon className="h-4 w-4" />
      <span className="sr-only">Next slide</span>
    </Button>
  )
})

CarouselNext.displayName = "CarouselNext"

export {
  Carousel,
  CarouselIndicator,
  CarouselIndicatorVideoIcon,
  CarouselMainContainer,
  CarouselNext,
  CarouselPrevious,
  CarouselThumbsContainer,
  SliderMainItem,
  SliderThumbItem,
  useCarousel,
}
